import { t } from 'i18next'
import { Avatar } from 'primereact/avatar'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { FileUpload } from 'primereact/fileupload'
import { Slider } from 'primereact/slider'
import { Toast } from 'primereact/toast'
import React, { useEffect, useRef, useState } from 'react'
import AvatarEditor from 'react-avatar-editor'
import {
  API_PROFILE_PICTURE_UPLOAD,
  API_PROFILE_PICTURE_UPLOAD_SELF,
} from '../../../constants'
import { RequestMethod } from '../../../model/enums/enums'
import { Apprentice, Person } from '../../../model/person.model'
import { State } from '../../../model/state.model'
import { updatePersonInStore } from '../../../redux/basic/updatePerson'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { handleRequest } from '../../../services/api/base/handleRequest'
import { getUserBigAvatarSrc } from '../../../util/getAvatar'
import { urlToFileParser } from '../../../util/urlToFileParser'
import {
  showCheckMarkToast,
  showErrorToast,
} from '../checkmark-toast/CheckmarkToast'
import { LoadingSpinner } from '../loading-spinner/LoadingSpinner'

export const PhotoEditDialog = (props: {
  hideEditProfilePictureDialog: () => void
  showProfilePictureEditor: boolean
  person: Person | undefined | Apprentice
  is_self?: boolean
}): JSX.Element => {
  const dispatcher = useAppDispatch()
  const fileUploadRef = useRef<FileUpload | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [scale, setScale] = useState<any>(1)
  const [rotate, setRotate] = useState<any>(0)
  const toast = useRef<Toast>(null)
  const [profileImage, setProfileImage] = useState<string>('')

  const { personId } = useAppSelector((state) => state.user)
  const userClaims = useAppSelector((state) => state.claims)
  // TODO specify claim
  const hasEditRight =
    personId === props.person?.person_id || userClaims?.includes('001-000-000')

  const resetPhotoAdjustment = () => {
    setScale(1)
    setRotate(0)
  }

  useEffect(() => {
    const startImage = getUserBigAvatarSrc(props.person)
    setProfileImage(startImage)
  }, [props.person])

  const handlePhotoUpload = (event: any): void => {
    const pId = props.person?.person_id
    if (!props.person || !pId) {
      console.warn(
        'Person ID not defined, this should not happen... better close this dialog'
      )
      props.hideEditProfilePictureDialog()
      return
    }

    const fileToUpload = event.files?.[0] // get the first

    if (!fileToUpload) {
      console.warn('no valid file')
      fileUploadRef?.current?.clear()
      return
    }

    if (fileToUpload.type.substring(0, 5) !== 'image') {
      console.warn('Filetype is no image type! Will not upload.')
      fileUploadRef?.current?.clear()
      resetPhotoAdjustment()
      return
    }

    setProfileImage(fileToUpload)
    fileUploadRef?.current?.clear()
    resetPhotoAdjustment()
  }

  const updateAndReset = (result: any) => {
    if (!props?.person) {
      console.warn('[Photo Editor] Person not defined, could not delete.')
      return
    }

    const personCopy = { ...props.person }

    const image = result.data.profile_image_ref
    const thumbnail = result.data.profile_image_thumbnail_ref

    personCopy.profile_image_ref = image ? image : ''
    personCopy.profile_image_thumbnail_ref = thumbnail ? thumbnail : ''
    updatePersonInStore(personCopy, dispatcher)

    fileUploadRef?.current?.clear()

    resetPhotoAdjustment()

    setProfileImage(result.data.profile_image_ref)

    showCheckMarkToast(toast)
    props.hideEditProfilePictureDialog()
  }

  /**
   * delete the Profile image ref in db + thumbnail and photo from the store
   * @returns {undefined}
   */
  const deleteProfilePhoto = (): void => {
    const pId = props.person?.person_id

    if (!pId) {
      console.warn(
        '[Photo Editor] Person id not defined, can not delete picture.'
      )
      return
    }

    const url = props.is_self
      ? API_PROFILE_PICTURE_UPLOAD_SELF()
      : API_PROFILE_PICTURE_UPLOAD(pId)

    handleRequest<any>(url, RequestMethod.DELETE)
      .then((res) => {
        console.log(
          '[Photo Editor] Successfully deleted the profile picture of person with ID: ' +
            pId
        )
        updateAndReset(res)

        const startImage = getUserBigAvatarSrc(props.person)
        setProfileImage(startImage)
      })
      .catch((error: any) => {
        showErrorToast(
          toast,
          t<string>('core.error.photo.delete.title'),
          t<string>('core.error.photo.delete.message')
        )
        console.error(
          `[Photo Editor] Could not delete the profile photo of person with id ${pId}`,
          error
        )
      })
  }

  const editor = useRef<AvatarEditor>(null)

  const onClickSave = async () => {
    if (editor) {
      // The image resized to the canvas size of 400px (a HTMLCanvasElement)
      const canvasScaled = editor?.current?.getImageScaledToCanvas()

      const sca = await canvasScaled?.toDataURL('image/png')
      const image = await urlToFileParser(sca, 'image.png')

      const pId = props.person?.person_id

      if (!image || !pId) {
        console.warn(
          '[Photo Editor] Image or person not defined, will not proceed.'
        )
        return
      }

      setLoading(true)

      const url = props.is_self
        ? API_PROFILE_PICTURE_UPLOAD_SELF()
        : API_PROFILE_PICTURE_UPLOAD(pId)

      handleRequest<any>(
        url,
        RequestMethod.UPLOAD_MEDIA,
        image,
        'profilepicture'
      )
        .then((res: State<any>) => {
          updateAndReset(res)
        })
        .catch((error: any) => {
          fileUploadRef?.current?.clear()
          console.error(error)
          showErrorToast(
            toast,
            t<string>('core.error.photo.update.title'),
            t<string>('core.error.photo.update.message')
          )
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const resetPhoto = () => {
    resetPhotoAdjustment()
    setProfileImage(getUserBigAvatarSrc(props.person))
    fileUploadRef.current?.clear()
  }

  return (
    <Dialog
      onHide={props.hideEditProfilePictureDialog}
      id={'upload-profile-image-dialog'}
      header={`${t<string>('hrm.upload_picture')} `}
      visible={props.showProfilePictureEditor}
      closable={true}
      closeOnEscape={true}
      className={'photo-edit-dialog justify-content-center'}
    >
      {!hasEditRight ? (
        <Avatar
          image={getUserBigAvatarSrc(props.person)}
          shape={'square'}
          className={'person-image-edit'}
        />
      ) : (
        <div className="photo-edit-dialog-container">
          {loading ? (
            <div className="photo-edit-loading-spinner-margin">
              <LoadingSpinner />
            </div>
          ) : (
            <AvatarEditor
              image={profileImage}
              width={350}
              height={350}
              border={10}
              borderRadius={360}
              color={[255, 255, 255, 0.75]} // RGBA
              scale={scale}
              backgroundColor={'#FFFFFF'}
              rotate={rotate}
              ref={editor}
              crossOrigin={'anonymous'}
            />
          )}
          <div className={'grid p-2 align-content-center align-items-center'}>
            <span className={'mr-4 fa-solid fa-search-plus icon-dark-gray'} />
            <Slider
              value={scale}
              onChange={(e) => setScale(e.value)}
              min={1}
              className={'min-width-120'}
              max={10}
              step={0.01}
            />
            <Button
              icon={'fa-solid fa-arrow-rotate-left'}
              className={'p-button-text p-button-help'}
              onClick={() => setScale(1)}
              tooltip={t<string>('core.reset')}
              tooltipOptions={{ showDelay: 600 }}
            />
          </div>
          <div className={'grid p-2 align-content-center align-items-center'}>
            <span className={'mr-4 fa-solid fa-rotate icon-dark-gray'} />
            <Slider
              value={rotate}
              onChange={(e) => setRotate(e.value)}
              min={-180}
              className={'min-width-120'}
              max={180}
              step={0.01}
            />
            <Button
              icon={'fa-solid fa-arrow-rotate-left'}
              className={'p-button-text p-button-help'}
              onClick={() => setRotate(0)}
              tooltip={t<string>('core.reset')}
              tooltipOptions={{ showDelay: 600 }}
            />
          </div>
          <div className={'grid p-2'}>
            <Button
              className={'p-button-danger p-button-outlined col m-1'}
              icon={'fas fa-trash'}
              disabled={!props.person?.profile_image_ref}
              tooltip={t<string>('core.delete')}
              tooltipOptions={{ showDelay: 600, position: 'top' }}
              onClick={() => deleteProfilePhoto()}
            />
            <Button
              className={'p-button p-button-outlined col m-1'}
              icon={'fa-solid fa-arrow-rotate-left'}
              tooltip={t<string>('core.reset')}
              tooltipOptions={{ showDelay: 600, position: 'top' }}
              onClick={() => resetPhoto()}
            />
            <FileUpload
              ref={fileUploadRef}
              name="profile_img_upload"
              className={'p-button-outlined'}
              customUpload={true}
              chooseOptions={{
                label: t<string>('hrm.choose_photo'),
                icon: 'fa-solid fa-images',
                className: 'col p-button-outlined m-1',
              }}
              mode={'basic'}
              auto
              uploadHandler={handlePhotoUpload}
              accept={'.jpeg, .png, .svg, .gif, .jpg, .webp, .tiff, .jfif'}
              multiple={false}
              disabled={!props.person?.person_id}
              invalidFileSizeMessageDetail={t<string>(
                'hrm.max_photo_size_15mb'
              )}
              maxFileSize={15000000}
            />
            <Button
              onClick={() => onClickSave()}
              className={'p-button m-1'}
              label={t<string>('hrm.set_photo')}
            />
          </div>
        </div>
      )}
      <Toast ref={toast} />
    </Dialog>
  )
}